import React, { useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import DirectoryHeader from './DirectoryHeader.jsx';

interface PendingRequest {
  id: string | number;
  first_name?: string;
  last_name?: string;
  driver_id?: string;
  email?: string;
  phone?: string;
  created_at?: string;
}

interface PendingRegistrationReviewProps {
  requests: PendingRequest[];
  loading: boolean;
  error: string | null;
  submittingId: string | number | null;
  onApprove: (requestId: string | number) => void;
  onReject: (requestId: string | number, reason: string) => void;
  onSendInvite?: (email: string) => Promise<{ ok: boolean; error?: string }>;
  onBack: () => void;
}

export default function PendingRegistrationReview({ requests, loading, error, submittingId, onApprove, onReject, onSendInvite, onBack }: PendingRegistrationReviewProps) {
  const [rejectReasons, setRejectReasons] = useState({});
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteResult, setInviteResult] = useState<string | null>(null);

  async function handleSendInvite() {
    if (!inviteEmail.trim() || !onSendInvite) return;
    setInviteSending(true);
    setInviteResult(null);
    const result = await onSendInvite(inviteEmail.trim());
    setInviteSending(false);
    if (result?.ok) {
      setInviteResult('Invite sent to ' + inviteEmail.trim());
      setInviteEmail('');
    } else {
      setInviteResult(result?.error || 'Failed to send invite');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <DirectoryHeader
        title="Pending registrations"
        subtitle={`${requests.length} pending requests`}
        onBack={onBack}
      />

      {onSendInvite && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
            Send registration invite
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Email</div>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => { setInviteEmail(e.target.value); setInviteResult(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && inviteEmail.includes('@')) handleSendInvite(); }}
                placeholder="driver@email.com"
                style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#fff', fontSize: 14, outline: 'none' }}
              />
            </div>
            <button
              type="button"
              onClick={handleSendInvite}
              disabled={!inviteEmail.includes('@') || inviteSending}
              style={{ padding: '10px 20px', borderRadius: 8, background: inviteEmail.includes('@') && !inviteSending ? C.gold : 'rgba(255,255,255,0.06)', color: inviteEmail.includes('@') && !inviteSending ? '#111' : 'rgba(255,255,255,0.2)', border: 'none', fontWeight: 700, fontSize: 13, cursor: inviteEmail.includes('@') && !inviteSending ? 'pointer' : 'not-allowed' }}
            >
              {inviteSending ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
          {inviteResult && <div style={{ color: inviteResult.startsWith('Invite sent') ? C.green : C.red, fontSize: 13, marginTop: 8 }}>{inviteResult}</div>}
        </div>
      )}

      {loading ? <div style={{ color: C.textMuted }}>Loading pending registrations...</div> : null}
      {error ? <div style={{ color: C.red }}>Pending registrations failed: {error}</div> : null}

      {!loading && !error ? (
        requests.length === 0 ? (
          <div style={LAYOUT.subPanel}>
            <div style={{ color: C.textMuted, fontSize: 13 }}>No pending registrations.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {requests.map((request: PendingRequest) => {
              const rejectReason = (rejectReasons as Record<string | number, string>)[request.id] || '';
              const isSubmitting = submittingId === request.id;
              const canReject = rejectReason.trim().length >= 3 && !isSubmitting;

              return (
                <div key={request.id} style={{ ...LAYOUT.subPanel, marginTop: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ color: C.text, fontSize: 16, fontWeight: 600 }}>{request.first_name} {request.last_name}</div>
                      <div style={{ color: C.textMuted, fontSize: 12, marginTop: 4 }}>
                        ID: {request.driver_id || '—'} · {request.email || 'No email'} · {request.phone || 'No phone'}
                      </div>
                      <div style={{ color: C.textMuted, fontSize: 11, marginTop: 6 }}>
                        Submitted: {request.created_at ? new Date(request.created_at).toLocaleString() : 'Unknown'}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => onApprove(request.id)}
                        style={{ padding: '8px 18px', background: C.green, color: C.navy, border: 'none', borderRadius: 8, cursor: isSubmitting ? 'not-allowed' : 'pointer', opacity: isSubmitting ? 0.5 : 1, fontWeight: 700 }}
                      >
                        {isSubmitting ? 'Working...' : 'Approve'}
                      </button>

                      <div style={{ display: 'grid', gap: 6, minWidth: 220 }}>
                        <textarea
                          value={rejectReason}
                          onChange={(event) => setRejectReasons((current) => ({ ...current, [request.id]: event.target.value }))}
                          placeholder="Rejection reason (required)"
                          style={{ minHeight: 60, resize: 'vertical', padding: 10, borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#fff' }}
                        />
                        <button
                          type="button"
                          disabled={!canReject}
                          onClick={() => onReject(request.id, rejectReason)}
                          style={{ padding: '8px 18px', background: C.red, color: '#fff', border: 'none', borderRadius: 8, cursor: canReject ? 'pointer' : 'not-allowed', opacity: canReject ? 1 : 0.5, fontWeight: 700 }}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : null}
    </div>
  );
}
